import { ReactNode, useEffect, useRef } from 'react';
import { useFeatureFlag } from 'configcat-react';
import { startAppInsights, trackTrace } from './telemetry';

interface AppInsightsGateProps {
  children: ReactNode;
}

// Delays App Insights startup until the enableTelemetry ConfigCat flag
// resolves true. While the flag is false, no telemetry is sent at all —
// this component only renders its children, it never blocks the app.
// Defaults to false so telemetry stays off unless ConfigCat explicitly
// enables it (e.g. if the flag is missing or ConfigCat is unreachable).
export const AppInsightsGate = ({ children }: AppInsightsGateProps) => {
  const { value: appInsightsEnabled } = useFeatureFlag(
    'enableTelemetry',
    false,
  );
  const hasTrackedInit = useRef(false);

  useEffect(() => {
    // startAppInsights() returns false (instead of throwing) when the
    // connection string is missing, so the app keeps running without telemetry.
    if (appInsightsEnabled && startAppInsights()) {
      // Fired here, after loadAppInsights() has run, so the telemetry
      // initializers (source/screen/operation ID) actually apply. Tracking
      // this from App.tsx's own mount effect raced the ConfigCat flag fetch
      // and produced trace events with no custom properties.
      if (!hasTrackedInit.current) {
        hasTrackedInit.current = true;
        trackTrace('App initialized Maintenance Frontend');
      }
    }
  }, [appInsightsEnabled]);

  return <>{children}</>;
};
